import { useState, useEffect } from 'react'
import {
  Box, Stack, TextInput, NumberInput, Switch, Button, Group, Text,
  Table, Badge, Alert, Divider, Skeleton, Select
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  IconSettings, IconPlugConnected, IconCheck, IconX, IconRefresh,
  IconArrowsExchange, IconUsers, IconAlertTriangle, IconDeviceFloppy
} from '@tabler/icons-react'
import { useJiraConfig, useJiraSyncStatus, useJiraStatusMappings, useJiraUserMappings } from '@/hooks/useJiraData'
import { jiraApi } from '@/api/jira'
import { useCurrentTenant, useCan } from '@/store/auth'
import { JiraCustomFieldsPanel } from './JiraCustomFieldsPanel'
import { useQueryClient } from '@tanstack/react-query'
import { WidgetCard, CardHeader } from '@/components/ui'
import { formatTimeAgo } from '@/utils/formatTimeAgo'

interface EditableMapping {
  consoleai_status: string
  jira_status_name: string
  jira_status_id: string
  direction: string
}

interface RemoteStatus {
  name: string
  id: string
}

const DIRECTION_OPTIONS = [
  { value: 'both', label: 'Bidirecional' },
  { value: 'to_jira', label: '→ Jira' },
  { value: 'from_jira', label: '← Jira' },
]

export function JiraConfigPanel() {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  const can = useCan()
  const queryClient = useQueryClient()

  // Permission guard
  if (!tenantId || !can('jira', 'admin')) return null

  // Data hooks
  const { data: config, isLoading: configLoading } = useJiraConfig()
  const { data: syncStatus } = useJiraSyncStatus()
  const { data: statusMappings } = useJiraStatusMappings()
  const { data: userMappings } = useJiraUserMappings()

  // Config form state
  const [baseUrl, setBaseUrl] = useState('')
  const [projectKey, setProjectKey] = useState('')
  const [secretArn, setSecretArn] = useState('')
  const [pollInterval, setPollInterval] = useState(300)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [toggling, setToggling] = useState(false)
  const [autoMatching, setAutoMatching] = useState(false)
  const [registeringWebhook, setRegisteringWebhook] = useState(false)

  // Editable status mappings state
  const [editMappings, setEditMappings] = useState<EditableMapping[]>([])
  const [remoteStatuses, setRemoteStatuses] = useState<RemoteStatus[]>([])
  const [savingMappings, setSavingMappings] = useState(false)

  // Load config into form
  useEffect(() => {
    if (config) {
      setBaseUrl(config.jira_base_url)
      setProjectKey(config.jira_project_key)
      setSecretArn(config.jira_secret_arn)
      setPollInterval(config.poll_interval_seconds)
    }
  }, [config])

  // Populate editMappings from statusMappings data
  useEffect(() => {
    if (statusMappings) {
      setEditMappings(statusMappings.map(m => ({
        consoleai_status: m.consoleai_status,
        jira_status_name: m.jira_status_name,
        jira_status_id: m.jira_status_id,
        direction: m.direction,
      })))
    }
  }, [statusMappings])

  // Fetch remote statuses when config exists
  useEffect(() => {
    if (config && tenantId) {
      jiraApi.getRemoteStatuses(tenantId).then((items) => {
        // items may be grouped by issue type — flatten to unique statuses
        const seen = new Set<string>()
        const flat: RemoteStatus[] = []
        const raw = items as Array<{ id?: string; name?: string; statuses?: Array<{ id: string; name: string }> }>
        raw.forEach((entry) => {
          // If entry has nested statuses array (grouped by issue type)
          if (entry.statuses && Array.isArray(entry.statuses)) {
            entry.statuses.forEach((s) => {
              if (!seen.has(s.id)) {
                seen.add(s.id)
                flat.push({ id: s.id, name: s.name })
              }
            })
          } else if (entry.id && entry.name) {
            // Direct status object
            if (!seen.has(entry.id)) {
              seen.add(entry.id)
              flat.push({ id: entry.id, name: entry.name })
            }
          }
        })
        setRemoteStatuses(flat)
      }).catch(() => {
        // Silently fail — remote statuses are optional
      })
    }
  }, [config, tenantId])

  const handleSave = async () => {
    setSaving(true)
    try {
      await jiraApi.upsertConfig(tenantId, {
        jira_base_url: baseUrl,
        jira_project_key: projectKey,
        jira_secret_arn: secretArn,
        poll_interval_seconds: pollInterval,
      })
      queryClient.invalidateQueries({ queryKey: ['jira-config'] })
      notifications.show({ title: 'Salvo', message: 'Configuração Jira atualizada', color: 'green' })
    } catch {
      notifications.show({ title: 'Erro', message: 'Falha ao salvar configuração', color: 'red' })
    }
    setSaving(false)
  }

  const handleTest = async () => {
    setTesting(true)
    setTestResult(null)
    try {
      const result = await jiraApi.testConnection(tenantId, {
        jira_base_url: baseUrl,
        jira_project_key: projectKey,
        jira_secret_arn: secretArn,
      })
      setTestResult({
        success: result.success,
        message: result.success
          ? `Conectado: ${result.project_name} (${result.total_issues} issues)`
          : `Erro: ${result.error}`,
      })
    } catch {
      setTestResult({ success: false, message: 'Falha na conexão' })
    }
    setTesting(false)
  }

  const handleToggle = async () => {
    setToggling(true)
    try {
      await jiraApi.toggleSync(tenantId, !config?.sync_enabled)
      queryClient.invalidateQueries({ queryKey: ['jira-config'] })
    } catch {
      notifications.show({ title: 'Erro', message: 'Falha ao alterar sync', color: 'red' })
    }
    setToggling(false)
  }

  const handleAutoMatch = async () => {
    setAutoMatching(true)
    try {
      const result = await jiraApi.autoMatchUsers(tenantId)
      queryClient.invalidateQueries({ queryKey: ['jira-user-mappings'] })
      notifications.show({
        title: 'Auto-match concluído',
        message: `${result.matched} matches, ${result.unmatched} sem match`,
        color: 'green',
      })
    } catch {
      notifications.show({ title: 'Erro', message: 'Falha no auto-match', color: 'red' })
    }
    setAutoMatching(false)
  }

  const handleRegisterWebhook = async () => {
    if (!tenantId) return
    setRegisteringWebhook(true)
    try {
      await jiraApi.registerWebhook(tenantId)
      queryClient.invalidateQueries({ queryKey: ['jira-config', tenantId] })
      notifications.show({ title: 'Webhook registrado', message: 'Webhook Jira ativado com sucesso', color: 'green' })
    } catch {
      notifications.show({ title: 'Erro', message: 'Falha ao registrar webhook', color: 'red' })
    } finally {
      setRegisteringWebhook(false)
    }
  }

  const handleDeregisterWebhook = async () => {
    if (!tenantId) return
    try {
      await jiraApi.deregisterWebhook(tenantId)
      queryClient.invalidateQueries({ queryKey: ['jira-config', tenantId] })
      notifications.show({ title: 'Webhook removido', message: 'Webhook Jira desregistrado', color: 'blue' })
    } catch {
      notifications.show({ title: 'Erro', message: 'Falha ao desregistrar webhook', color: 'red' })
    }
  }

  const handleSaveMappings = async () => {
    setSavingMappings(true)
    try {
      await jiraApi.upsertStatusMappings(tenantId, editMappings)
      queryClient.invalidateQueries({ queryKey: ['jira-status-mappings'] })
      notifications.show({ title: 'Mapeamentos salvos', message: 'Status mapeados com sucesso', color: 'green' })
    } catch {
      notifications.show({ title: 'Erro', message: 'Falha ao salvar mapeamentos', color: 'red' })
    }
    setSavingMappings(false)
  }

  const updateMapping = (index: number, field: keyof EditableMapping, value: string) => {
    setEditMappings(prev => prev.map((m, i) => {
      if (i !== index) return m
      const updated = { ...m, [field]: value }
      // When Jira status changes, update both name and id
      if (field === 'jira_status_id') {
        const found = remoteStatuses.find(s => s.id === value)
        if (found) {
          updated.jira_status_name = found.name
          updated.jira_status_id = found.id
        }
      }
      return updated
    }))
  }

  const remoteStatusOptions = remoteStatuses.map(s => ({ value: s.id, label: s.name }))

  if (configLoading) return <Skeleton height={400} />

  return (
    <Stack gap="xl">
      {/* Section 1: Configuration */}
      <WidgetCard accent="info" p={0}>
        <CardHeader title="Configuração Jira" extra={<IconSettings size={16} />} />
        <Box p="md">
          <Stack gap="sm">
            <TextInput label="URL da instância" placeholder="https://empresa.atlassian.net" value={baseUrl} onChange={(e) => setBaseUrl(e.currentTarget.value)} />
            <TextInput label="Projeto" placeholder="TEF" value={projectKey} onChange={(e) => setProjectKey(e.currentTarget.value)} />
            <TextInput label="Secret ARN" placeholder="arn:aws:secretsmanager:..." value={secretArn} onChange={(e) => setSecretArn(e.currentTarget.value)} />
            <NumberInput label="Intervalo de polling (segundos)" value={pollInterval} onChange={(v) => setPollInterval(Number(v) || 300)} min={60} max={3600} />

            <Group gap="sm">
              <Button onClick={handleTest} loading={testing} variant="light" leftSection={<IconPlugConnected size={14} />}>
                Testar Conexão
              </Button>
              <Button onClick={handleSave} loading={saving} leftSection={<IconCheck size={14} />}>
                Salvar
              </Button>
            </Group>

            {testResult && (
              <Alert color={testResult.success ? 'green' : 'red'} icon={testResult.success ? <IconCheck size={16} /> : <IconX size={16} />}>
                {testResult.message}
              </Alert>
            )}

            <Divider my="xs" />
            <Group>
              <Text size="sm">Sync:</Text>
              <Switch
                checked={config?.sync_enabled ?? false}
                onChange={handleToggle}
                disabled={toggling || !config}
                label={config?.sync_enabled ? 'Ativo' : 'Inativo'}
                color="teal"
              />
            </Group>
          </Stack>
        </Box>
      </WidgetCard>

      {/* Section 2: Status Mapping (editable) */}
      <WidgetCard accent="none" p={0}>
        <CardHeader title="Mapeamento de Status" extra={<IconArrowsExchange size={16} />} />
        <Box p="md">
          {editMappings.length > 0 ? (
            <Stack gap="sm">
              <Table verticalSpacing="xs" fz="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>ConsoleAI</Table.Th>
                    <Table.Th>→</Table.Th>
                    <Table.Th>Jira</Table.Th>
                    <Table.Th>Direção</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {editMappings.map((m, idx) => (
                    <Table.Tr key={idx}>
                      <Table.Td>
                        <Badge variant="light" color="blue">{m.consoleai_status}</Badge>
                      </Table.Td>
                      <Table.Td>→</Table.Td>
                      <Table.Td>
                        {remoteStatusOptions.length > 0 ? (
                          <Select
                            data={remoteStatusOptions}
                            value={m.jira_status_id}
                            onChange={(v) => v && updateMapping(idx, 'jira_status_id', v)}
                            size="xs"
                            w={180}
                            placeholder="Selecionar status"
                            searchable
                          />
                        ) : (
                          <Badge variant="light" color="grape">{m.jira_status_name}</Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Select
                          data={DIRECTION_OPTIONS}
                          value={m.direction}
                          onChange={(v) => v && updateMapping(idx, 'direction', v)}
                          size="xs"
                          w={140}
                        />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              <Group justify="flex-end">
                <Button
                  onClick={handleSaveMappings}
                  loading={savingMappings}
                  size="xs"
                  leftSection={<IconDeviceFloppy size={14} />}
                >
                  Salvar Mapeamentos
                </Button>
              </Group>
            </Stack>
          ) : (
            <Text c="dimmed" size="sm">Nenhum mapeamento configurado. Configure a conexão primeiro.</Text>
          )}
        </Box>
      </WidgetCard>

      {/* Section 3: User Mapping */}
      <WidgetCard accent="none" p={0}>
        <CardHeader title="Mapeamento de Usuários" extra={<IconUsers size={16} />} />
        <Box p="md">
          <Stack gap="sm">
            <Button onClick={handleAutoMatch} loading={autoMatching} variant="light" leftSection={<IconRefresh size={14} />} size="xs">
              Auto-match por Email
            </Button>
            {userMappings && userMappings.length > 0 ? (
              <Table verticalSpacing="xs" fz="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>ConsoleAI User</Table.Th>
                    <Table.Th>Jira User</Table.Th>
                    <Table.Th>Método</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {userMappings.map((m) => (
                    <Table.Tr key={m.id}>
                      {/* TODO: enrich with user name requires a backend /users list endpoint */}
                      <Table.Td>User #{m.consoleai_user_id}</Table.Td>
                      <Table.Td>{m.jira_display_name ?? m.jira_account_id ?? '—'}</Table.Td>
                      <Table.Td><Badge size="xs" variant="light">{m.match_method}</Badge></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            ) : (
              <Text c="dimmed" size="sm">Nenhum mapeamento. Use o botão acima para auto-match.</Text>
            )}
          </Stack>
        </Box>
      </WidgetCard>

      {/* Section 4: Custom Fields */}
      <WidgetCard accent="none" p={0}>
        <CardHeader title="Campos Customizados" extra={<IconArrowsExchange size={16} />} />
        <Box p="md">
          <JiraCustomFieldsPanel />
        </Box>
      </WidgetCard>

      {/* Section 5: Sync Status */}
      <WidgetCard accent={syncStatus?.queue_failed ? 'warning' : 'none'} p={0}>
        <CardHeader title="Status do Sync" extra={<IconRefresh size={16} />} />
        <Box p="md">
          {syncStatus ? (
            <Stack gap="sm">
              <Group gap="xl">
                <Box>
                  <Text size="xs" c="dimmed">Pendentes</Text>
                  <Text fw={700}>{syncStatus.queue_pending}</Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed">Falhas</Text>
                  <Text fw={700} c={syncStatus.queue_failed > 0 ? 'red' : undefined}>
                    {syncStatus.queue_failed}
                  </Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed">Último sync</Text>
                  <Text size="sm">{formatTimeAgo(syncStatus.last_sync_at ?? null)}</Text>
                </Box>
              </Group>
              {syncStatus.recent_errors.length > 0 && (
                <>
                  <Divider my="xs" />
                  <Text size="sm" fw={500} c="red">Erros recentes</Text>
                  {syncStatus.recent_errors.map((err) => (
                    <Alert key={err.id} color="red" icon={<IconAlertTriangle size={14} />} py="xs">
                      <Text size="xs">{err.operation}: {err.last_error}</Text>
                    </Alert>
                  ))}
                </>
              )}
            </Stack>
          ) : (
            <Text c="dimmed" size="sm">Configure o Jira primeiro.</Text>
          )}
        </Box>
      </WidgetCard>

      {/* Section 6: Webhook Config */}
      <WidgetCard accent="none" p={0}>
        <CardHeader title="Webhook / Real-time" extra={<IconPlugConnected size={16} />} />
        <Box p="md">
          <Stack gap="sm">
            <Group>
              <Badge size="sm" color={config?.sync_enabled ? 'green' : 'gray'} variant="dot">
                {config?.sync_enabled ? 'Polling ativo' : 'Polling inativo'}
              </Badge>
            </Group>
            <Group gap="xs">
              <Button
                size="xs"
                variant="light"
                onClick={handleRegisterWebhook}
                loading={registeringWebhook}
                leftSection={<IconPlugConnected size={14} />}
              >
                Registrar Webhook
              </Button>
              <Button
                size="xs"
                variant="subtle"
                color="red"
                onClick={handleDeregisterWebhook}
                leftSection={<IconX size={14} />}
              >
                Desregistrar
              </Button>
            </Group>
            <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={14} />}>
              <Text size="xs">Webhooks requerem projeto org-managed no Jira. Team-managed usa polling.</Text>
            </Alert>
          </Stack>
        </Box>
      </WidgetCard>
    </Stack>
  )
}
